import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const ManufacturingFinalizationButton = ({ donor, onFinalize, disabled }) => {
  const [open, setOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState('');

  const isFinalized = donor?.manufacturingFinalized || false;
  const hasManufacturedVials = donor?.manufacturedVials?.length > 0;
  const totalVials = donor?.manufacturedVials?.reduce((sum, v) => sum + (v.quantity || 0), 0) || 0;

  const handleFinalize = async () => {
    setFinalizing(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/finalize-manufacturing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to finalize manufacturing');
      }

      const data = await response.json();
      
      if (onFinalize) {
        await onFinalize(data.data);
      }

      setOpen(false);
    } catch (err) {
      console.error('Error finalizing manufacturing:', err);
      setError(err.message || 'Failed to finalize manufacturing');
    } finally {
      setFinalizing(false);
    }
  };

  const canFinalize = hasManufacturedVials && !isFinalized && !disabled;

  const getFinalizationChecks = () => {
    const checks = [
      {
        label: 'Manufacturing records uploaded',
        status: hasManufacturedVials ? 'complete' : 'incomplete',
        description: hasManufacturedVials 
          ? `${totalVials} vials manufactured across ${donor?.manufacturedVials?.length || 0} product types`
          : 'No manufactured vials recorded'
      },
      {
        label: 'Processing records uploaded',
        status: (donor?.processingRecords?.length || 0) > 0 ? 'complete' : 'warning',
        description: `${donor?.processingRecords?.length || 0} processing records uploaded`
      }
    ];

    return checks;
  };

  if (isFinalized) {
    return (
      <Button
        variant="outlined"
        startIcon={<LockIcon />}
        disabled
        sx={{ 
          color: 'success.main',
          borderColor: 'success.main',
          '&.Mui-disabled': {
            color: 'success.main',
            borderColor: 'success.main',
            opacity: 0.7
          }
        }}
      >
        Manufacturing Finalized
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AssignmentIcon />}
        onClick={() => setOpen(true)}
        disabled={!canFinalize}
      >
        Finalize Manufacturing
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6">Finalize Manufacturing</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Typography variant="body1" sx={{ mb: 3 }}>
            Finalizing manufacturing will lock the manufacturing data and allow progression to QC Testing.
            This action cannot be undone.
          </Typography>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Pre-finalization Checklist
          </Typography>

          <List>
            {getFinalizationChecks().map((check, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemIcon>
                    {check.status === 'complete' ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <WarningIcon color="warning" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={check.label}
                    secondary={check.description}
                  />
                </ListItem>
                {index < getFinalizationChecks().length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          <Alert 
            severity="info" 
            sx={{ mt: 2 }}
            icon={<LockIcon />}
          >
            <Typography variant="body2">
              <strong>After finalization:</strong>
              <br />
              • Manufacturing data will be locked and cannot be modified
              • QC Testing tab will become available
              • Manufacturing workflow will be marked as complete
            </Typography>
          </Alert>

          {!hasManufacturedVials && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Cannot finalize manufacturing without manufactured vials. Please add manufactured products first.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={finalizing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFinalize}
            disabled={finalizing || !hasManufacturedVials}
            startIcon={finalizing ? <CircularProgress size={20} /> : <LockIcon />}
          >
            {finalizing ? 'Finalizing...' : 'Finalize Manufacturing'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManufacturingFinalizationButton;